/**
 * Sim-lab annotation store — docs/SIM_LAB_ANNOTATE.md
 * Neon primary when signed in; localStorage cache + seed fallback.
 */
import { R1AS_ANNOTATIONS_SEED } from '../data/r1asAnnotations';
import { readStoredSession } from './nativeAuth';

export const ANNOTATION_KINDS = [
  'faucet-hit',
  'faucet-spout',
  'stove-display',
  'note',
];

const storageKey = (slug) => `simlab.annotations.${slug || 'unknown'}`;

function seedForSlug(slug) {
  if (slug === 'r1as') {
    return (R1AS_ANNOTATIONS_SEED || []).map((a) => normalizePin(a));
  }
  return [];
}

function normalizePin(a) {
  if (!a || typeof a !== 'object') return a;
  let userEmail = typeof a.userEmail === 'string' ? a.userEmail : '';
  // Seed placeholder → author (docs/SIM_LAB_ANNOTATE.md)
  if (userEmail === 'seed@buildconstrux.com') {
    userEmail = 'jordan.j@buildconstrux.com';
  }
  return {
    ...a,
    notes: typeof a.notes === 'string' ? a.notes : '',
    label: typeof a.label === 'string' ? a.label : '',
    stationId: a.stationId || '',
    userEmail,
    userId: a.userId || null,
  };
}

/** @returns {object[]} */
export function loadAnnotations(slug) {
  if (typeof window === 'undefined' || !slug) return [...seedForSlug(slug)];
  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (!raw) return [...seedForSlug(slug)];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...seedForSlug(slug)];
    return parsed.map(normalizePin);
  } catch {
    return [...seedForSlug(slug)];
  }
}

export function saveAnnotations(slug, list) {
  if (typeof window === 'undefined' || !slug) return;
  try {
    window.localStorage.setItem(
      storageKey(slug),
      JSON.stringify((list || []).map(normalizePin))
    );
  } catch {
    /* quota / private mode */
  }
}

export function clearAnnotations(slug) {
  if (typeof window === 'undefined' || !slug) return;
  try {
    window.localStorage.removeItem(storageKey(slug));
  } catch {
    /* ignore */
  }
}

export function makeAnnotationId() {
  return `ann_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function authHeaders() {
  const session = readStoredSession();
  if (!session?.accessToken) return null;
  return {
    Authorization: `Bearer ${session.accessToken}`,
    'Content-Type': 'application/json',
  };
}

async function parseJson(res) {
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return { ok: false, error: 'unexpected response' };
  }
  try {
    return await res.json();
  } catch {
    return { ok: false, error: 'invalid json' };
  }
}

/**
 * Load pins for a slug: Neon when authed, else local/seed.
 * @returns {Promise<{ annotations: object[], source: 'neon' | 'local' }>}
 */
export async function syncLoadAnnotations(slug) {
  const cached = loadAnnotations(slug);
  const headers = authHeaders();
  if (!headers || !slug) {
    return { annotations: cached, source: 'local' };
  }

  try {
    const res = await fetch(
      `/api/lab-annotations?slug=${encodeURIComponent(slug)}`,
      { headers: { Authorization: headers.Authorization } }
    );
    const data = await parseJson(res);
    if (!res.ok || !data.ok || !Array.isArray(data.annotations)) {
      return { annotations: cached, source: 'local' };
    }
    let list = data.annotations.map(normalizePin);
    const remoteIds = new Set(list.map((a) => a.id));
    const localOnly = cached.filter((a) => a?.id && !remoteIds.has(a.id));
    if (localOnly.length > 0) {
      // Push browser-only pins so Neon becomes the shared source of truth.
      await Promise.all(
        localOnly.map((pin) =>
          fetch('/api/lab-annotations', {
            method: 'POST',
            headers,
            body: JSON.stringify(pin),
          }).catch(() => null)
        )
      );
      const again = await fetch(
        `/api/lab-annotations?slug=${encodeURIComponent(slug)}`,
        { headers: { Authorization: headers.Authorization } }
      );
      const againData = await parseJson(again);
      if (again.ok && againData.ok && Array.isArray(againData.annotations)) {
        list = againData.annotations.map(normalizePin);
      }
    }
    saveAnnotations(slug, list);
    return { annotations: list, source: 'neon' };
  } catch {
    return { annotations: cached, source: 'local' };
  }
}

/** @returns {Promise<{ ok: boolean, annotation?: object, source: string }>} */
export async function syncCreateAnnotation(pin) {
  const headers = authHeaders();
  const normalized = normalizePin(pin);
  if (!headers) {
    return { ok: false, annotation: normalized, source: 'local' };
  }
  try {
    const res = await fetch('/api/lab-annotations', {
      method: 'POST',
      headers,
      body: JSON.stringify(normalized),
    });
    const data = await parseJson(res);
    if (!res.ok || !data.ok) {
      return { ok: false, annotation: normalized, source: 'local' };
    }
    return {
      ok: true,
      annotation: normalizePin(data.annotation || normalized),
      source: 'neon',
    };
  } catch {
    return { ok: false, annotation: normalized, source: 'local' };
  }
}

/** @returns {Promise<{ ok: boolean, annotation?: object, source: string }>} */
export async function syncPatchAnnotation(id, patch) {
  const headers = authHeaders();
  if (!headers) {
    return { ok: false, source: 'local' };
  }
  try {
    const res = await fetch('/api/lab-annotations', {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ id, ...patch }),
    });
    const data = await parseJson(res);
    if (!res.ok || !data.ok) {
      return { ok: false, source: 'local' };
    }
    return {
      ok: true,
      annotation: normalizePin(data.annotation),
      source: 'neon',
    };
  } catch {
    return { ok: false, source: 'local' };
  }
}

/** @returns {Promise<{ ok: boolean, source: string }>} */
export async function syncDeleteAnnotation(id) {
  const headers = authHeaders();
  if (!headers || !id) {
    return { ok: false, source: 'local' };
  }
  try {
    const res = await fetch(
      `/api/lab-annotations?id=${encodeURIComponent(id)}`,
      { method: 'DELETE', headers: { Authorization: headers.Authorization } }
    );
    const data = await parseJson(res);
    return { ok: res.ok && data.ok, source: res.ok ? 'neon' : 'local' };
  } catch {
    return { ok: false, source: 'local' };
  }
}

/** @returns {Promise<{ ok: boolean, source: string }>} */
export async function syncClearAnnotations(slug) {
  const headers = authHeaders();
  clearAnnotations(slug);
  if (!headers || !slug) {
    return { ok: true, source: 'local' };
  }
  try {
    const res = await fetch(
      `/api/lab-annotations?slug=${encodeURIComponent(slug)}`,
      { method: 'DELETE', headers: { Authorization: headers.Authorization } }
    );
    const data = await parseJson(res);
    return { ok: res.ok && data.ok, source: res.ok ? 'neon' : 'local' };
  } catch {
    return { ok: false, source: 'local' };
  }
}

/**
 * Explicit `faucet-spout` pin only (last wins).
 * @param {object[]} annotations
 * @param {string} stationId
 * @returns {[number, number, number] | null}
 */
export function spoutPinFromAnnotations(annotations, stationId) {
  const spoutPins = (annotations || []).filter(
    (a) => a.stationId === stationId && a.kind === 'faucet-spout' && a.position
  );
  if (!spoutPins.length) return null;
  return spoutPins[spoutPins.length - 1].position;
}

/**
 * Spout origin for a station.
 * Prefers `faucet-spout`; optionally offsets from last `faucet-hit`.
 * @param {object[]} annotations
 * @param {string} stationId
 * @param {{ allowHitFallback?: boolean }} [opts]
 * @returns {[number, number, number] | null}
 */
export function spoutFromAnnotations(annotations, stationId, opts = {}) {
  const pin = spoutPinFromAnnotations(annotations, stationId);
  if (pin) return pin;

  if (opts.allowHitFallback === false) return null;

  const hits = hitsFromAnnotations(annotations, stationId);
  if (!hits.length) return null;
  const hit = hits[hits.length - 1];
  const [x, y, z] = hit.position;
  const n = hit.normal || [0, 1, 0];
  const len = Math.hypot(n[0], n[1], n[2]) || 1;
  const lift = 0.035;
  return [
    x + (n[0] / len) * lift,
    y + (n[1] / len) * lift,
    z + (n[2] / len) * lift,
  ];
}

/**
 * @param {object[]} annotations
 * @param {string} stationId
 */
export function hitsFromAnnotations(annotations, stationId) {
  return (annotations || []).filter(
    (a) => a.stationId === stationId && a.kind === 'faucet-hit' && a.position
  );
}
